/**
 * React permission plumbing that reuses the backend authorization model.
 *
 * Frontend action-gating must never be a divergent copy of the server policy.
 * This module supplies the current {@link AuthorizationSubject} through React
 * context and exposes a {@link useCan} hook that defers every decision to
 * {@link can} from `src/server/authorization.ts` — the same table-driven engine
 * the backend enforces with. A component asking "may the current user do X?"
 * therefore gets exactly the answer the server would give.
 *
 * @module components/PermissionContext
 */

import React, { useContext } from "react";

import type { Action, AuthorizationSubject, Resource } from "@/server/authorization";
import { can } from "@/server/authorization";

/**
 * React context carrying the current {@link AuthorizationSubject}.
 *
 * `null` marks "no provider in the tree", which {@link useCan} treats as a
 * programming error so the UI never silently makes decisions without a subject.
 */
const PermissionContext = React.createContext<AuthorizationSubject | null>(null);

/**
 * Props for {@link PermissionProvider}.
 */
export interface PermissionProviderProps {
  /** The authorization subject frontend permission checks are derived from. */
  subject: AuthorizationSubject;
  /** The subtree that may consult {@link useCan}. */
  children: React.ReactNode;
}

/**
 * Supply the current {@link AuthorizationSubject} to descendant components.
 *
 * Wrap any subtree whose action-gating should follow the backend permission
 * model, then read decisions with {@link useCan}.
 */
export function PermissionProvider({ subject, children }: PermissionProviderProps) {
  return <PermissionContext.Provider value={subject}>{children}</PermissionContext.Provider>;
}

/**
 * Read the current {@link AuthorizationSubject} from context.
 *
 * @returns The subject supplied by the nearest {@link PermissionProvider}.
 * @throws {Error} When called outside a {@link PermissionProvider}.
 */
export function usePermissionSubject(): AuthorizationSubject {
  const subject = useContext(PermissionContext);
  if (subject === null) {
    throw new Error("usePermissionSubject must be used within a <PermissionProvider>");
  }
  return subject;
}

/**
 * Decide whether the current subject may perform an action on a resource.
 *
 * The result is computed by {@link can} against the subject from the nearest
 * {@link PermissionProvider}, so the UI gates actions with the identical policy
 * the server enforces.
 *
 * @param action - The protected action being gated.
 * @param resource - The resource the action targets.
 * @returns `true` when the action is permitted, otherwise `false`.
 */
export function useCan(action: Action, resource: Resource): boolean {
  const subject = usePermissionSubject();
  return can(subject, action, resource);
}

/**
 * {@link useCan} with {@link PermissionGate}'s rule for a missing provider:
 * permissive, so a control rendered outside one keeps showing.
 */
export function useCanOrPermissive(action: Action, resource: Resource): boolean {
  const subject = useContext(PermissionContext);
  return subject === null ? true : can(subject, action, resource);
}

/**
 * Props for {@link PermissionGate}.
 */
export interface PermissionGateProps {
  /** The protected action the gated children require. */
  action: Action;
  /** The resource the action targets. */
  resource: Resource;
  /** The controls rendered only when the action is permitted. */
  children: React.ReactNode;
}

/**
 * Render `children` only when the current subject may perform `action` on
 * `resource`, and render nothing when the decision denies it.
 *
 * The allow/deny decision is the same {@link can} result the backend enforces
 * for the subject supplied by the nearest {@link PermissionProvider}, so a gated
 * control is hidden exactly when the server would refuse the action. When no
 * {@link PermissionProvider} is present the gate is permissive — the UI gate is
 * a best-effort convenience and the server remains the source of truth — so
 * existing unguarded render paths keep showing their controls.
 *
 * @example
 * ```tsx
 * <PermissionGate action="programme:write" resource={{ type: "programme", programmeId }}>
 *   <button>Edit</button>
 * </PermissionGate>
 * ```
 */
export function PermissionGate({ action, resource, children }: PermissionGateProps) {
  const allowed = useCanOrPermissive(action, resource);
  return allowed ? <>{children}</> : null;
}
